import React, { useState, useEffect } from "react";
import { View, StyleSheet, ScrollView, Platform } from "react-native";
import { useToast } from "../../../contexts/Toast/ToastProvider";
import { useForm, Controller } from "react-hook-form";
import CustomAdminModal from "../../../components/common/general/CustomAdminModal/CustomAdminModal";
import CustomText from "../../../components/common/general/CustomText/CustomText";
import CustomTextInput from "../../../components/common/general/CustomTextInput/CustomTextInput";
import CustomButton from "../../../components/common/general/CustomButton/CustomButton";
import CustomSpacing from "../../../components/common/layout/CustomSpacing/CustomSpacing";
import { DatePickerInput } from "react-native-paper-dates";
import {
  useCreateAnnouncementMutation,
  useUpdateAnnouncementMutation,
  AnnouncementScope,
  AudienceType,
} from "../../../graphql/generated/graphql";
import { useAppConfig } from "../../../AppConfigProvider";
import CustomRichTextEditor from "../../TextEditor/CustomRichTextEditor";

interface AnnouncementFormModalProps {
  visible: boolean;
  onClose: () => void;
  courseId: string;
  announcement?: any;
}

const AnnouncementFormModal: React.FC<AnnouncementFormModalProps> = ({ visible, onClose, courseId, announcement }) => {
  const { primaryColor } = useAppConfig();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [moveToInactive, setMoveToInactive] = useState(false);
  const [messageCharCount, setMessageCharCount] = useState(0);

  const { showToast } = useToast();

  const MAX_MESSAGE_LENGTH = 1000;
  const isEditing = !!announcement;
  const isExpired = announcement?.expirationDate && new Date(announcement.expirationDate) < new Date();

  const [createAnnouncement] = useCreateAnnouncementMutation();
  const [updateAnnouncement] = useUpdateAnnouncementMutation();

  const [activelyEditing, setActivelyEditing] = useState(false);

  const {
    control,
    handleSubmit,
    watch,
    setValue,
    reset,
    formState: { errors },
  } = useForm({
    defaultValues: {
      title: "",
      message: "",
      expirationDate: null,
    },
  });

  const watchTitle = watch("title");
  const watchMessage = watch("message");
  const watchExpirationDate = watch("expirationDate");

  const stripHtmlTags = (html: string) => {
    const withoutTags = html.replace(/<[^>]*>/g, "");
    const tempDiv = document.createElement("div");
    tempDiv.innerHTML = withoutTags;
    return tempDiv.textContent || tempDiv.innerText || "";
  };

  // Update character count when message changes
  useEffect(() => {
    const plainText = stripHtmlTags(watchMessage || "");
    setMessageCharCount(plainText.length);
  }, [watchMessage]);

  useEffect(() => {
    if (visible && announcement) {
      reset({
        title: announcement.title || "",
        message: announcement.message || "",
        expirationDate: announcement.expirationDate ? new Date(announcement.expirationDate) : null,
      });
      setMoveToInactive(false);
    } else if (visible && !announcement) {
      reset({
        title: "",
        message: "",
        expirationDate: null,
      });
      setMoveToInactive(false);
      setMessageCharCount(0);
    }
  }, [visible, announcement, reset]);

  const handleMoveToInactiveToggle = () => {
    const newValue = !moveToInactive;
    setMoveToInactive(newValue);

    if (newValue) {
      setValue("expirationDate", new Date());
    } else {
      setValue("expirationDate", null);
    }
  };

  const handleClearExpiration = () => {
    setValue("expirationDate", null);
  };

  const onSubmit = async (data) => {
    if (!data.title.trim()) {
      showToast({
        message: "Please enter an announcement title.",
        type: "error",
      });
      return;
    }

    if (!data.message.trim()) {
      showToast({
        message: "Please enter an announcement message.",
        type: "error",
      });
      return;
    }

    const plainTextMessage = stripHtmlTags(data.message);
    if (plainTextMessage.length > MAX_MESSAGE_LENGTH) {
      showToast({
        message: `Message cannot exceed ${MAX_MESSAGE_LENGTH} characters.`,
        type: "error",
      });
      return;
    }

    setIsSubmitting(true);

    try {
      if (isEditing) {
        await updateAnnouncement({
          variables: {
            id: announcement.id,
            input: {
              title: data.title,
              message: data.message,
              expirationDate: data.expirationDate?.toISOString() || null,
              scope: AnnouncementScope.Course,
              audienceType: AudienceType.Students,
              courseId,
            },
          },
        });
      } else {
        await createAnnouncement({
          variables: {
            input: {
              title: data.title,
              message: data.message,
              expirationDate: data.expirationDate?.toISOString() || null,
              scope: AnnouncementScope.Course,
              audienceType: AudienceType.Students,
              courseId,
            },
          },
        });
      }

      onClose();
    } catch (error) {
      console.error("Error saving announcement:", error);
      showToast({
        message: "Failed to save announcement. Please try again.",
        type: "error",
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleCancel = () => {
    if (!isSubmitting) {
      onClose();
    }
  };

  const isMessageTooLong = messageCharCount > MAX_MESSAGE_LENGTH;
  const isNearLimit = messageCharCount > MAX_MESSAGE_LENGTH * 0.8;

  const renderContent = () => (
    <>
      {isEditing && announcement?.isActive && !isExpired && (
        <>
          <View style={styles.toggleRow}>
            <CustomButton
              styleType="transparent"
              style={styles.checkboxButton}
              leftIcon={
                moveToInactive ? (
                  <View style={[styles.checkbox, styles.checkboxChecked]}>
                    <CustomText text="✓" style={styles.checkmark} useTranslationText={false} />
                  </View>
                ) : (
                  <View style={styles.checkbox} />
                )
              }
              onPress={handleMoveToInactiveToggle}
              disabled={isSubmitting}
            />
            <View style={styles.toggleContent}>
              <CustomText
                text="Move to Inactive"
                weight="bold"
                size="m"
                style={styles.toggleTitle}
                useTranslationText={false}
              />
              <CustomText
                text="Students will no longer see this on the course page."
                size="s"
                style={styles.toggleDescription}
                useTranslationText={false}
              />
            </View>
          </View>
          <CustomSpacing type="vertical" size="l" />
        </>
      )}

      {isEditing && isExpired && (
        <>
          <View style={styles.expiredNotice}>
            <CustomText
              text={`This announcement expired on ${new Date(announcement.expirationDate).toLocaleDateString()} and was automatically moved to Inactive.`}
              size="s"
              style={styles.expiredText}
              useTranslationText={false}
            />
            <CustomText
              text="To make it active again, change or clear the expiration date."
              size="s"
              style={styles.expiredText}
              useTranslationText={false}
            />
          </View>
          <CustomSpacing type="vertical" size="l" />
        </>
      )}

      <View style={styles.formGroup}>
        <CustomText text="Announcement Title" size="m" weight="ultraBold" useTranslationText={false} />
        <CustomText
          text="Short headline that summarizes the message."
          size="s"
          style={styles.fieldDescription}
          useTranslationText={false}
        />
        <CustomSpacing type="vertical" size="s" />
        <Controller
          name="title"
          control={control}
          rules={{ required: true }}
          render={({ field: { onChange, value } }) => (
            <CustomTextInput
              value={value}
              onChangeText={onChange}
              placeholder="e.g., Reminder: Midterm Project Due Friday"
              error={errors.title?.message}
            />
          )}
        />
      </View>

      <CustomSpacing type="vertical" size="l" />

      <View style={styles.formGroup}>
        <View style={styles.messageHeader}>
          <CustomText text="Message" size="m" weight="ultraBold" useTranslationText={false} />
          <View style={styles.characterCounter}>
            <CustomText
              text={`${messageCharCount}/${MAX_MESSAGE_LENGTH}`}
              size="xs"
              style={[
                styles.characterCountText,
                isMessageTooLong && styles.characterCountError,
                isNearLimit && !isMessageTooLong && styles.characterCountWarning,
              ]}
              useTranslationText={false}
            />
          </View>
        </View>
        <CustomText
          text="Include any important details. Students will see this at the top of the course page."
          size="s"
          style={styles.fieldDescription}
          useTranslationText={false}
        />
        <CustomSpacing type="vertical" size="s" />
        <Controller
          name="message"
          control={control}
          rules={{
            required: true,
            validate: (value) => {
              const plainText = stripHtmlTags(value || "");
              if (plainText.length > MAX_MESSAGE_LENGTH) {
                return `Message cannot exceed ${MAX_MESSAGE_LENGTH} characters`;
              }
              return true;
            },
          }}
          render={({ field: { onChange, value } }) => {
            const handleMessageChange = (content: string) => {
              setActivelyEditing(true);
              onChange(content);
              setValue("message", content, { shouldDirty: true, shouldValidate: false });
              const plainText = stripHtmlTags(content || "");
              setMessageCharCount(plainText.length);
            };

            return (
              <CustomRichTextEditor
                value={value}
                onChange={handleMessageChange}
                isSubmitting={isSubmitting}
                placeholder="e.g., Make sure to review the rubric and upload your final project by 11:59 PM Friday. Reach out if you have questions!"
                activelyEditing={activelyEditing}
                restrictImageUpload={true}
              />
            );
          }}
        />
        {isMessageTooLong && (
          <CustomText
            text={`Message is ${messageCharCount - MAX_MESSAGE_LENGTH} characters too long. Please shorten your message.`}
            size="xs"
            style={styles.errorText}
            useTranslationText={false}
          />
        )}
      </View>

      <CustomSpacing type="vertical" size="l" />

      <View style={styles.formGroup}>
        <CustomText text="Expiration Date (Optional)" size="m" weight="ultraBold" useTranslationText={false} />
        <CustomText
          text="The announcement will move to Inactive after this date."
          size="s"
          style={styles.fieldDescription}
          useTranslationText={false}
        />
        <CustomSpacing type="vertical" size="s" />
        <CustomText text="Exp. Date" size="s" weight="ultraBold" useTranslationText={false} />
        <CustomSpacing type="vertical" size="xs" />
        <Controller
          name="expirationDate"
          control={control}
          rules={{ required: false }}
          render={({ field: { onChange, value } }) => (
            <DatePickerInput
              locale="en"
              value={value}
              onChange={onChange}
              inputMode="start"
              mode="outlined"
              outlineColor={"#D1D5DB"}
              selectionColor={false}
              activeOutlineColor={"blue"}
              withDateFormatInLabel={false}
              placeholder="MM/DD/YYYY"
              inputProps={{
                color: "#000000",
                mode: "outline",
              }}
              theme={{
                colors: "#000000",
                roundness: 8,
                opacity: 1,
              }}
              style={styles.datePicker}
              aria-label="Expiration date picker"
            />
          )}
        />
        <CustomSpacing type="vertical" size="s" />
        {watchExpirationDate && (
          <CustomButton
            text="Clear Expiration"
            styleType="transparent"
            size="s"
            onPress={handleClearExpiration}
            style={styles.clearButton}
            textStyle={{ color: primaryColor }}
          />
        )}
      </View>
    </>
  );
  return (
    <CustomAdminModal
      visible={visible}
      onRequestClose={handleCancel}
      ariaLabel={isEditing ? "Edit Announcement Modal" : "Add Announcement Modal"}
      title={isEditing ? "Edit Announcement" : "Add Announcement"}
      style={[styles.modal, Platform.OS === "web" ? ({ maxHeight: "90vh" } as any) : { maxHeight: "90%" }]}>
      <View style={styles.modalBody}>
        <ScrollView
          showsVerticalScrollIndicator
          style={styles.scroll}
          contentContainerStyle={styles.scrollContentContainer}
          keyboardShouldPersistTaps="handled"
          tabIndex={0}>
          {renderContent()}
        </ScrollView>

        <View style={styles.buttonContainer}>
          <View style={styles.buttonWrapper}>
            <CustomButton
              text="Cancel"
              onPress={handleCancel}
              styleType="primaryLight"
              size="m"
              disabled={isSubmitting}
              style={styles.cancelButton}
              bold
            />
            <CustomButton
              text={
                isSubmitting
                  ? isEditing
                    ? "Saving..."
                    : "Posting..."
                  : isEditing
                    ? "Save Changes"
                    : "Post Announcement"
              }
              onPress={handleSubmit(onSubmit)}
              styleType="primary"
              size="m"
              disabled={isSubmitting || !watchTitle.trim() || !watchMessage.trim() || isMessageTooLong}
              style={[
                styles.submitButton,
                {
                  backgroundColor:
                    isSubmitting || !watchTitle.trim() || !watchMessage.trim() || isMessageTooLong
                      ? "#E5E7EB"
                      : primaryColor,
                },
              ]}
              textStyle={{
                color:
                  isSubmitting || !watchTitle.trim() || !watchMessage.trim() || isMessageTooLong
                    ? "#6B7280"
                    : "#FFFFFF",
              }}
              bold
            />
          </View>
        </View>
      </View>
    </CustomAdminModal>
  );
};

const styles = StyleSheet.create({
  modal: {
    width: "90%",
    maxWidth: 600,
    padding: 0,
    alignSelf: "center",
  },
  modalBody: {
    minHeight: 0,
    maxHeight: "100%",
    width: "100%",
  },
  scroll: {
    backgroundColor: "#FFFFFF",
    paddingHorizontal: 24,
    paddingTop: 24,
  },
  scrollContentContainer: {
    paddingBottom: 24,
    flexGrow: 1,
  },
  toggleRow: {
    flexDirection: "row",
    alignItems: "center",
    backgroundColor: "#F0FDF4",
    padding: 16,
    borderRadius: 8,
    borderWidth: 1,
    borderColor: "#BBF7D0",
  },
  checkboxButton: {
    padding: 0,
    marginRight: 12,
    marginTop: 2,
  },
  checkbox: {
    width: 16,
    height: 16,
    borderWidth: 2,
    borderColor: "#D1D5DB",
    borderRadius: 4,
    backgroundColor: "#FFFFFF",
    justifyContent: "center",
    alignItems: "center",
  },
  checkboxChecked: {
    backgroundColor: "#22C55E",
    borderColor: "#22C55E",
  },
  checkmark: {
    color: "#FFFFFF",
    fontSize: 10,
    fontWeight: "bold",
  },
  toggleContent: {
    flex: 1,
  },
  toggleTitle: {
    color: "#111827",
    marginBottom: 4,
  },
  toggleDescription: {
    color: "#6B7280",
    lineHeight: 18,
  },
  expiredNotice: {
    backgroundColor: "#F2F4F7",
    paddingHorizontal: 16,
    paddingVertical: 8,
    borderRadius: 12,
  },
  expiredText: {
    color: "#667085",
    lineHeight: 20,
  },
  formGroup: {
    width: "100%",
  },
  fieldDescription: {
    color: "#6B7280",
    marginTop: 4,
  },
  messageHeader: {
    flexDirection: "row",
    justifyContent: "space-between",
    alignItems: "center",
  },
  characterCounter: {
    backgroundColor: "#F8F9FA",
    paddingHorizontal: 8,
    paddingVertical: 4,
    borderRadius: 4,
  },
  characterCountText: {
    color: "#6B7280",
    fontWeight: "500",
  },
  characterCountWarning: {
    color: "#F59E0B",
  },
  characterCountError: {
    color: "#EF4444",
  },
  errorText: {
    color: "#EF4444",
    marginTop: 4,
  },
  datePicker: {
    borderWidth: 0,
    backgroundColor: "#F8F8F8",
    borderColor: "#000000",
    borderRadius: 10,
    color: "#000000",
    fontSize: 14,
  },
  clearButton: {
    alignSelf: "flex-start",
    padding: 0,
  },
  buttonContainer: {
    backgroundColor: "#FFFFFF",
    borderTopWidth: 1,
    borderTopColor: "#E5E7EB",
    paddingHorizontal: 24,
    paddingVertical: 16,
    flexShrink: 0,
  },
  buttonWrapper: {
    flexDirection: "row",
    justifyContent: "space-between",
    alignItems: "center",
  },
  cancelButton: {
    backgroundColor: "transparent",
    borderWidth: 2,
    borderColor: "#3E68FE",
    borderRadius: 8,
    paddingHorizontal: 16,
  },
  submitButton: {
    borderRadius: 8,
    paddingHorizontal: 24,
  },
});

export default AnnouncementFormModal;
